import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./ExpressSettingsPage.module.css";
import useAuthGuard from "../../../hooks/useAuthGuard";
import {
  getExpressSettingsApi,
  updateExpressSettingsApi,
} from "../../../api/api";

const ExpressSettingsPage = () => {
  useAuthGuard();
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const [settings, setSettings] = useState({
    is_express_enabled: true,
    marina_express_enabled: false,
    jumeirah_express_enabled: true,
    dubaihills_express_enabled: false,
    whatsapp_express_enabled: true,
    cutoff_hour: 17,
    updated_at: null,
  });

  const [initialSettings, setInitialSettings] = useState(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await getExpressSettingsApi();
      if (response && response.data) {
        setSettings(response.data);
        setInitialSettings(response.data);
      }
    } catch (err) {
      console.error("Error fetching express settings:", err);
      showMsg("error", "Failed to load express settings. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem("adminRole");
    setRole(userRole || "");
    if (userRole === "superadmin" || userRole === "admin") {
      fetchSettings();
    } else {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => {
      setMsg({ type: "", text: "" });
    }, 4000);
  };

  const handleToggle = (key) => {
    setSettings((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleCutoffChange = (e) => {
    const val = parseInt(e.target.value, 10);
    setSettings((prev) => ({
      ...prev,
      cutoff_hour: val,
    }));
  };

  const handleReset = () => {
    if (initialSettings) {
      setSettings(initialSettings);
      showMsg("success", "Settings reverted to last saved state.");
    }
  };

  const handleSave = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      showMsg("error", "Authentication token missing. Please log in again.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        is_express_enabled: settings.is_express_enabled,
        marina_express_enabled: settings.marina_express_enabled,
        jumeirah_express_enabled: settings.jumeirah_express_enabled,
        dubaihills_express_enabled: settings.dubaihills_express_enabled,
        whatsapp_express_enabled: settings.whatsapp_express_enabled,
        cutoff_hour: settings.cutoff_hour,
      };

      const response = await updateExpressSettingsApi(payload, token);
      if (response && response.data) {
        showMsg("success", "Express booking settings saved successfully!");
        if (response.data.settings) {
          setSettings(response.data.settings);
          setInitialSettings(response.data.settings);
        }
      }
    } catch (err) {
      console.error("Error updating express settings:", err);
      const errMsg =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to save settings. Please try again.";
      showMsg("error", errMsg);
    } finally {
      setSaving(false);
    }
  };

  if (role && role !== "superadmin" && role !== "admin") {
    return (
      <div className={styles.container}>
        <div className={styles.card}>
          <h2>Access Denied</h2>
          <p>You must be an Admin or Super Admin to view and modify Express Booking settings.</p>
          <button className={styles.btnSecondary} onClick={() => navigate("/dashboard")}>
            &larr; Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const formatHourDisplay = (hour) => {
    if (hour === 12) return "12:00 PM (Noon)";
    if (hour > 12) return `${hour - 12}:00 PM`;
    return `${hour}:00 AM`;
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.pageTitle}>⚡ Express Booking Settings</h1>
        <p className={styles.pageSubtitle}>
          Configure Express speed availability across stores, channels, and daily cutoff hours.
        </p>
      </header>

      {msg.text && (
        <div className={msg.type === "success" ? styles.alertSuccess : styles.alertError}>
          <span>{msg.type === "success" ? "✓" : "⚠️"}</span>
          <span>{msg.text}</span>
        </div>
      )}

      {loading ? (
        <div className={styles.card} style={{ textAlign: "center", padding: "40px" }}>
          <p>Loading Express settings...</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {/* Section 1: System Master Switch */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.cardTitle}>Global Master Switch</h3>
                <p className={styles.cardDesc}>
                  Turn Express booking completely on or off across all customer channels and locations.
                </p>
              </div>
              <span className={settings.is_express_enabled ? styles.badgeEnabled : styles.badgeDisabled}>
                {settings.is_express_enabled ? "ACTIVE" : "DISABLED"}
              </span>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Allow Express Bookings Globally
                </div>
                <p className={styles.settingDescription}>
                  When disabled, Express Speed is hidden or disallowed on all customer booking portals and WhatsApp.
                </p>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.is_express_enabled}
                  onChange={() => handleToggle("is_express_enabled")}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>

          {/* Section 2: Store Locations */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.cardTitle}>Store Location Availability</h3>
                <p className={styles.cardDesc}>
                  Select which shop locations accept Express Speed drop-offs and pickups.
                </p>
              </div>
            </div>

            {/* Marina Store */}
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Marina Store
                  <span className={settings.marina_express_enabled ? styles.badgeEnabled : styles.badgeDisabled}>
                    {settings.marina_express_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className={styles.settingDescription}>
                  Enable or disable Express stringing and grip service at the Marina location.
                  {!settings.marina_express_enabled && (
                    <strong style={{ color: "#c2410c", display: "block", marginTop: "3px" }}>
                      Currently disabled: customers choosing Marina will only see Regular service.
                    </strong>
                  )}
                </p>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.marina_express_enabled}
                  onChange={() => handleToggle("marina_express_enabled")}
                  disabled={!settings.is_express_enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            {/* Jumeirah Store */}
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Jumeirah Store (Main Hub)
                  <span className={settings.jumeirah_express_enabled ? styles.badgeEnabled : styles.badgeDisabled}>
                    {settings.jumeirah_express_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className={styles.settingDescription}>
                  Primary stringing hub with dedicated full-time stringers.
                </p>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.jumeirah_express_enabled}
                  onChange={() => handleToggle("jumeirah_express_enabled")}
                  disabled={!settings.is_express_enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            {/* Dubai Hills Store */}
            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Dubai Hills Store
                  <span className={settings.dubaihills_express_enabled ? styles.badgeEnabled : styles.badgeDisabled}>
                    {settings.dubaihills_express_enabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <p className={styles.settingDescription}>
                  When enabled, customers can book Express service at the Dubai Hills Store.
                </p>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.dubaihills_express_enabled}
                  onChange={() => handleToggle("dubaihills_express_enabled")}
                  disabled={!settings.is_express_enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>

          {/* Section 3: WhatsApp Channel Controls */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.cardTitle}>WhatsApp Booking Channel</h3>
                <p className={styles.cardDesc}>
                  Control Express service availability specifically for customers booking via WhatsApp links.
                </p>
              </div>
              <span className={settings.whatsapp_express_enabled ? styles.badgeEnabled : styles.badgeDisabled}>
                {settings.whatsapp_express_enabled ? "Enabled" : "Disabled"}
              </span>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Allow Express on WhatsApp Form (/whatsapp-booking)
                </div>
                <p className={styles.settingDescription}>
                  When active, customers on WhatsApp can select Express Speed before the daily cutoff hour.
                </p>
              </div>
              <label className={styles.switch}>
                <input
                  type="checkbox"
                  checked={settings.whatsapp_express_enabled}
                  onChange={() => handleToggle("whatsapp_express_enabled")}
                  disabled={!settings.is_express_enabled}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          </div>

          {/* Section 4: Daily Cutoff Time */}
          <div className={styles.card}>
            <div className={styles.cardHeader}>
              <div>
                <h3 className={styles.cardTitle}>Same-Day Cutoff Time</h3>
                <p className={styles.cardDesc}>
                  Express bookings for same-day completion are blocked after this time each day.
                </p>
              </div>
            </div>

            <div className={styles.settingItem}>
              <div className={styles.settingInfo}>
                <div className={styles.settingLabel}>
                  Daily Cutoff Hour (UAE Local Time)
                </div>
                <p className={styles.settingDescription}>
                  Currently set to <strong>{formatHourDisplay(settings.cutoff_hour)}</strong>. After this time, customers booking for today are informed that Express service has reached cutoff and only Regular Speed is available.
                </p>
              </div>
              <div className={styles.cutoffWrapper}>
                <select
                  value={settings.cutoff_hour}
                  onChange={handleCutoffChange}
                  className={styles.cutoffSelect}
                  disabled={!settings.is_express_enabled}
                >
                  <option value={14}>2:00 PM (14:00)</option>
                  <option value={15}>3:00 PM (15:00)</option>
                  <option value={16}>4:00 PM (16:00)</option>
                  <option value={17}>5:00 PM (17:00) — Default</option>
                  <option value={18}>6:00 PM (18:00)</option>
                  <option value={19}>7:00 PM (19:00)</option>
                  <option value={20}>8:00 PM (20:00)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Action Bar */}
          <div className={styles.actionsBar}>
            <div className={styles.timestamp}>
              {settings.updated_at && (
                <span>Last updated: {new Date(settings.updated_at).toLocaleString()}</span>
              )}
            </div>
            <div className={styles.buttonGroup}>
              <button
                type="button"
                className={styles.btnSecondary}
                onClick={handleReset}
                disabled={saving}
              >
                Reset
              </button>
              <button
                type="button"
                className={styles.btnPrimary}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className={styles.loadingSpinner}></span>
                    Saving...
                  </>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpressSettingsPage;
